import {
  BLOCKED_TYPE_TEXT,
  BlocklistProctorInformationFields,
  calculateBlockedUntilDate,
  EmployeeInformationFields,
} from '@exam-center/blocked-user-management';
import {
  BlockedRecordBlockedScope,
  GetBlockedRecordByBlockedUserIdDocument,
  GetBlockedUserByIdDocument,
  useGetAllEnabledExamCoursesQuery,
  useUpdateBlockedRecordMutation,
} from '@exam-center/graphql';
import {
  Divider,
  Flex,
  Form,
  FormButton,
  type OptionSelectionProps,
  PaperSection,
  SectionTitle,
  Text,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { formatDate } from '@shared/utils';
import { useEffect, useMemo, useState } from 'react';

import { useBlockedRecordDetails } from '../../providers';
import {
  type BlockedRecordStaffFormInput,
  useBlockedRecordStaffFormContext,
} from '../../providers/blocked-record-staff-form-provider';

const BLOCKED_SCOPE_OPTIONS: OptionSelectionProps[] = [
  {
    label: 'ระงับสิทธิ์ทุกหลักสูตร',
    value: BlockedRecordBlockedScope.AllScope,
  },
  { label: 'ระงับสิทธิ์เฉพาะหลักสูตรที่เลือก', value: BlockedRecordBlockedScope.PartialScope },
];

export default function BlockedRecordStaffFormLogic() {
  const { showPromiseSnackbar } = useSnackbar();
  const { blockedRecord, blockedUser } = useBlockedRecordDetails();

  const [blockedUntil, setBlockedUntil] = useState<Date | null>(blockedRecord.blockedUntil);
  const [fieldVisibilityStatus, setFieldVisibilityStatus] = useState<{
    examCourses: boolean;
    enforcementEndDuration: boolean;
  }>({
    examCourses: blockedRecord.blockedScope === BlockedRecordBlockedScope.PartialScope,
    enforcementEndDuration: !!blockedRecord.blockedUntil,
  });

  const formContext = useBlockedRecordStaffFormContext();

  const [updateBlockedRecord, { loading: isUpdating }] = useUpdateBlockedRecordMutation({
    refetchQueries: [
      GetBlockedRecordByBlockedUserIdDocument,
      {
        query: GetBlockedUserByIdDocument,
        variables: {
          input: {
            id: blockedUser.id,
          },
        },
      },
    ],
  });

  const handleUpdateBlockedRecord = async (data: BlockedRecordStaffFormInput) => {
    const examCourseCodes =
      data.blockedCourses?.type === BlockedRecordBlockedScope.PartialScope
        ? data.blockedCourses?.courses?.map((course) => course.value)
        : undefined;

    const isAllCoursesBlocked = data.blockedCourses?.type === BlockedRecordBlockedScope.AllScope;
    if (isAllCoursesBlocked) {
      formContext.setValue('blockedCourses.courses', []);
    }

    const isEnforcementEndAssign = data.enforcementEnd?.assign === 'true';
    if (!isEnforcementEndAssign) {
      formContext.setValue('enforcementEnd.date', undefined);
    }

    const enforcementEndDate = isEnforcementEndAssign ? data.enforcementEnd?.date : null;

    return await updateBlockedRecord({
      variables: {
        input: {
          blockedRecordId: blockedRecord.id,
          info: {
            blockedScope: data.blockedCourses?.type,
            examCourseCodes,
            blockedReason: data.remark === '' ? undefined : data.remark,
            blockedUntil: calculateBlockedUntilDate({
              blockedType: blockedRecord.blockedType,
              enforcementEndDate,
            }),
            position: data.position === '' ? undefined : data.position,
            department: data.department === '' ? undefined : data.department,
            enforcementEndDate,
          },
        },
      },
    });
  };

  const onSubmit = async (data: BlockedRecordStaffFormInput) => {
    showPromiseSnackbar({
      message: {
        pending: 'กำลังบันทึกข้อมูล',
        success: 'บันทึกข้อมูลเรียบร้อยแล้ว',
        error: 'บันทึกข้อมูลไม่สำเร็จ',
      },
      promise: () => handleUpdateBlockedRecord(data),
    });
  };

  const { data: examCoursesData, loading: isLoadingCourses } = useGetAllEnabledExamCoursesQuery();
  const examCourseOptions = useMemo(() => {
    const examCourses = examCoursesData?.ecGetAllEnabledExamCourses.examCourses ?? [];

    return examCourses.map((course) => ({
      label: `${course.code}: ${course.name}`,
      value: course.code,
    }));
  }, [examCoursesData]);

  useEffect(() => {
    // REMARK: handle clear value
    const subscription = formContext.watch((value, { name }) => {
      // REMARK: handle clear value when blockedCourse type changes
      if (name === 'blockedCourses.type') {
        if (value.blockedCourses?.type !== BlockedRecordBlockedScope.PartialScope) {
          formContext.resetField('blockedCourses.courses');
        }
      }

      // REMARK: handle clear value when enforcementEnd assign changes
      if (name === 'enforcementEnd.assign') {
        if (value.enforcementEnd?.assign === 'false') {
          formContext.resetField('enforcementEnd.date');
        }
      }

      // REMARK: handle calculate blockedUntil when enforcementEnd date changes
      if (name === 'enforcementEnd.date') {
        if (value.enforcementEnd?.date) {
          const blockedUntil = calculateBlockedUntilDate({
            blockedType: blockedRecord.blockedType,
            enforcementEndDate: value.enforcementEnd.date ?? null,
          });
          setBlockedUntil(blockedUntil);
        } else {
          setBlockedUntil(null);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [formContext, blockedRecord.blockedType]);

  useEffect(() => {
    const subscription = formContext.watch((value, { name }) => {
      if (name === 'blockedCourses.type') {
        setFieldVisibilityStatus((prev) => ({
          ...prev,
          examCourses: value.blockedCourses?.type === BlockedRecordBlockedScope.PartialScope,
        }));
      }

      if (name === 'enforcementEnd.assign') {
        setFieldVisibilityStatus((prev) => ({
          ...prev,
          enforcementEndDuration: value?.enforcementEnd?.assign === 'true',
        }));
      }
    });

    return () => subscription.unsubscribe();
  }, [formContext]);

  useEffect(() => {
    if (formContext.formState.isSubmitSuccessful) {
      formContext.reset(formContext.getValues());
    }
  }, [formContext.formState.isSubmitSuccessful, formContext]);

  return (
    <Form onSubmit={formContext.handleSubmit(onSubmit)}>
      <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(6) })}>
        <PaperSection title='ข้อมูลการระงับสิทธิ์สอบ'>
          <Flex
            sx={(theme) => ({
              flexDirection: 'column',
              gap: theme.spacing(6),
              p: theme.spacing(6),
            })}
          >
            <SectionTitle title='Person Information' />
            <EmployeeInformationFields
              fields={{
                blockedType: {
                  mode: 'view',
                  value: BLOCKED_TYPE_TEXT[blockedRecord.blockedType],
                },
                position: { mode: 'input', name: 'position' },
                department: { mode: 'input', name: 'department' },
              }}
              visibleFields={{
                position: true,
                department: true,
              }}
            />
          </Flex>
          <Divider />
          <BlocklistProctorInformationFields
            fields={{
              startedAt: { mode: 'view', value: formatDate(blockedRecord.blockedAt) },
              blockedCourseType: {
                mode: 'input',
                name: 'blockedCourses.type',
                slotProps: {
                  options: BLOCKED_SCOPE_OPTIONS,
                },
              },
              blockedCourses: {
                name: 'blockedCourses.courses',
                options: examCourseOptions,
                required: true,
                loading: isLoadingCourses,
              },
              endedAt: {
                mode: 'input',
                assign: {
                  name: 'enforcementEnd.assign',
                },
                date: {
                  name: 'enforcementEnd.date',
                  required: true,
                  hidden: !fieldVisibilityStatus.enforcementEndDuration,
                },
              },
              remark: { mode: 'input', name: 'remark' },
            }}
            options={{
              shouldDisplayBlockedCourseSelection: fieldVisibilityStatus.examCourses,
            }}
            slots={{
              endedAtDateEndAdornment: (
                <Text
                  variant='body2'
                  sx={{
                    height: '48px',
                    alignSelf: 'flex-end',
                    alignContent: 'center',
                  }}
                >
                  {`(วันที่สิ้นสุดระงับสิทธิ์: ${blockedUntil ? formatDate(blockedUntil) : 'DD-MM-YYYY'})`}
                </Text>
              ),
            }}
          />
        </PaperSection>
        <Flex sx={(theme) => ({ flex: 1, justifyContent: 'flex-end', gap: theme.spacing(3) })}>
          <FormButton label='บันทึก' size='large' loading={isUpdating} />
        </Flex>
      </Flex>
    </Form>
  );
}
